namespace $.$$ {

	type $shm_wordstat_job = {
		phase: string
		index: number
		fetched: number
		queue?: readonly $shm_wordstat_task[]
		error?: string
		log_keys?: Record< string, boolean >
	}

	export class $shm_wordstat_dashboard extends $.$shm_wordstat_dashboard {

		user_id() {
			return super.user_id() || this.$.$shm_ba_pb.user_id()
		}

		wordstat_user_record() {
			const user = this.user_id()
			if( !user ) return null
			return this.$.$shm_ba_pb.records_realtime( `users\nid = "${ this.$.$shm_ba_pb.filter_text( user ) }"` )[0] ?? null
		}

		@ $mol_mem
		state() {
			const obj = new this.$.$shm_wordstat_filter_state_store()
			obj.dashboard_id = ()=> `${ this.dashboard_id() }:collect`
			obj.arg_name = ()=> ''
			return this.state_setup( obj, true )
		}

		@ $mol_mem
		filter_state() {
			const obj = new this.$.$shm_wordstat_filter_state_store()
			obj.dashboard_id = ()=> this.dashboard_id()
			return this.state_setup( obj, false )
		}

		state_setup( obj: $shm_wordstat_filter_state_store, collect: boolean ) {
			obj.period_all = ()=> this.period_all()
			const defaults = obj.defaults.bind( obj )
			obj.defaults = ()=> {
				const base = defaults()
				const settings = this.task_settings()
				const facets = collect ? null : this.task_item_facets()
				const period_kind = settings?.period_kind ?? 'PERIOD_MONTHLY'
				const available_period = facets?.periods.includes( period_kind )
					? period_kind
					: this.period_kind_codes().find( period => facets?.periods.includes( period ) ) ?? period_kind
				return {
					... base,
					queries: facets ? facets.queries.join( '\n' ) : settings?.queries ?? base.queries,
					regions: facets ? facets.regions.join( '\n' ) : settings?.regions ?? base.regions,
					period_kind: available_period,
					period_kinds: facets
						? Object.fromEntries( this.period_kind_codes().map( period => [ period, period === available_period ] ) )
						: settings?.period_kinds ?? {
							PERIOD_DAILY: period_kind === 'PERIOD_DAILY',
							PERIOD_WEEKLY: period_kind === 'PERIOD_WEEKLY',
							PERIOD_MONTHLY: period_kind === 'PERIOD_MONTHLY',
						},
					devices: facets
						? Object.fromEntries( this.device_codes().map( device => [ device, facets.devices.includes( device ) ] ) )
						: settings?.devices ?? base.devices,
					period: this.period_all(),
				}
			}
			return obj
		}

		@ $mol_mem
		data() {
			const obj = new this.$.$shm_wordstat_data()
			obj.app_id = ()=> `wordstat_${ this.dashboard_id() }`
			obj.file_name = ()=> this.task_file().opfs_name()
			obj.file_source = ()=> this.task_file()
			obj.queries = ()=> this.queries_selected()
			obj.regions = ()=> this.regions_selected()
			obj.periods = ()=> this.period_kinds_selected()
			obj.devices = ()=> this.devices_filter_selected()
			obj.date_from = ()=> this.period().from || '0001-01-01'
			obj.date_to = ()=> this.period().to || '2999-12-31'
			return obj
		}

		@ $mol_mem
		task_file() {
			const obj = new this.$.$shm_wordstat_task_file()
			obj.task_id = ()=> this.dashboard_id()
			obj.version = ()=> this.task_result_version()
			obj.uri = ()=> this.task_result_uri()
			obj.token = ()=> this.$.$shm_ba_pb.auth_token()
			return obj
		}

		@ $mol_mem
		task_result_version() {
			return String( this.task_record()?.result_version || '' )
		}

		@ $mol_mem
		task_result_uri() {
			const record = this.task_record()
			if( !record ) return ''
			const file = this.$.$shm_ba_pb.record_files( record, 'result_file' )[0]
			return file ? this.$.$shm_ba_pb.file_uri( record, 'wordstat_tasks', file ) : ''
		}

		new_task_id() {
			return 'new'
		}

		task_record() {
			const id = this.dashboard_id()
			if( !id || id === this.new_task_id() ) return null
			return this.$.$shm_ba_pb.records_page_realtime_live(
				`wordstat_tasks\nid = "${ this.$.$shm_ba_pb.filter_text( id ) }"\n\n1\n1\nid,user,title,sort,mode,settings,result_file,result_version,collect_status,collect_total,collect_done,collect_progress,collect_error,collect_run_id,collect_started_at,collect_finished_at,regular_daily,regular_daily_last_at,reports_enabled,telegram_chat_ids,max_chat_ids,reports_last_at,updated`,
				`wordstat_tasks/task:${ id }`,
				record => String( record.id || '' ) === id,
			)[0] ?? null
		}

		task_settings() {
			return ( this.task_record()?.settings ?? null ) as Partial< {
				queries: string
				regions: string
				period_kind: string
				period_kinds: $shm_wordstat_filter
				devices: $shm_wordstat_filter
			} > | null
		}

		task_mode() {
			const arg = String( this.$.$mol_state_arg.value( 'wordstat_mode' ) || '' )
			if( arg === 'view' || arg === 'edit' ) {
				const mode = this.task_mode_normalize( arg )
				if( mode !== arg ) this.$.$mol_state_arg.value( 'wordstat_mode', mode )
				return mode
			}

			const stored = String( this.$.$mol_state_local.value( this.task_mode_key() ) || '' )
			if( stored === 'view' || stored === 'edit' ) {
				const mode = this.task_mode_normalize( stored )
				this.$.$mol_state_arg.value( 'wordstat_mode', mode )
				return mode
			}

			const mode = String( this.task_record()?.mode || '' )
			return this.task_mode_normalize( mode === 'view' ? 'view' : 'edit' )
		}

		task_mode_normalize( mode: 'edit' | 'view' ) {
			return mode === 'view' && !this.task_mode_view_enabled() ? 'edit' : mode
		}

		task_mode_key_for( id: string ) {
			return `hitalama_wordstat_task_mode:${ id }`
		}

		task_mode_key() {
			return this.task_mode_key_for( this.dashboard_id() )
		}

		collect_items_loaded() {
			return Boolean( this.dashboard_id() === this.new_task_id() || this.task_record() )
		}

		dashboard_pages() {
			this.collect_completion_sync()
			return this.task_mode() === 'edit'
				? [ this.Collect_page(), this.Collects_page() ]
				: [ this.Report_page(), this.Filters_page() ]
		}

		report_tools() {
			return [ this.Csv_download() ]
		}

		dashboard_title() {
			if( this.dashboard_id() === this.new_task_id() ) return 'Добавить задачу'
			return this.task_title_current().trim() || 'Wordstat'
		}

		tab_title() {
			const progress = this.collect_active()
				? ` (${ this.collect_items_done_count() }/${ this.collect_items_total_count() })`
				: ''
			return `${ this.dashboard_title() }${ progress } Wordstat`
		}

		task_title_current( id = this.dashboard_id() ) {
			if( id === this.new_task_id() ) return this.task_title().trim() || 'Задача'
			return String( this.task_record()?.title ?? this.task_title() ?? 'Wordstat' )
		}

		task_title_draft( next?: string ) {
			return this.task_title_draft_by_id( this.dashboard_id(), next )
		}

		@ $mol_mem_key
		task_title_draft_by_id( id: string, next?: string ) {
			$mol_wire_solid()
			return next ?? this.task_title_current( id )
		}

		@ $mol_action
		task_title_from_query( next?: FocusEvent ) {
			if( next === undefined ) return null

			const id = this.dashboard_id()
			if( !id || id === this.new_task_id() ) return next
			if( !/^Задача \d+$/.test( this.task_title_current( id ).trim() ) ) return next

			const title = this.collect_queries_selected()[0]?.trim() || ''
			if( !title ) return next

			this.$.$shm_ba_pb.update_record( 'wordstat_tasks', id, { title } )
			this.task_title_draft_by_id( id, title )
			return next
		}

		query_table() {
			return this.table_region_titles( this.data().query_table(), true )
		}

		query_summary() {
			return this.data().query_summary()
		}

		collect_items_table() {
			return {
				head: [ 'Запрос', 'Готово' ],
				rows: this.collect_items_rows(),
			}
		}

		collects_title() {
			return `Сбор данных (${ this.collect_items_done_count() } из ${ this.collect_items_total_count() })${ this.collect_duration_title() }`
		}

		collects_title_content() {
			return [
				this.Collects_title(),
				this.Collects_quota(),
				...( this.collect_active() ? [ this.Collect_cancel() ] : [] ),
			]
		}

		quota_bucket() {
			const start = new Date( this.$.$mol_state_time.now( 60_000 ) )
			start.setUTCMinutes( 0, 0, 0 )
			return {
				hour: start.toISOString(),
				reset: new Date( start.getTime() + 60 * 60 * 1000 ),
			}
		}

		quota_record() {
			const user = this.user_id()
			if( !user ) return null
			const hour = this.quota_bucket().hour
			const filter = `user = "${ this.$.$shm_ba_pb.filter_text( user ) }" && hour = "${ hour }"`
			return this.$.$shm_ba_pb.records_page_realtime_live(
				`wordstat_usage\n${ filter }\n\n1\n1\nid,user,hour,requests`,
				`wordstat_usage/hour:${ user }:${ hour }`,
				record => String( record.user || '' ) === user && String( record.hour || '' ) === hour,
			)[0] ?? null
		}

		quota_title() {
			const limit = Math.max( 0, Number( this.wordstat_user_record()?.wordstat_hourly_request_limit || 0 ) )
			const used = Math.max( 0, Number( this.quota_record()?.requests || 0 ) )
			const reset = new Intl.DateTimeFormat( 'ru-RU', {
				timeZone: 'Europe/Moscow',
				hour: '2-digit',
				minute: '2-digit',
			} ).format( this.quota_bucket().reset )
			const minutes = Math.max( 1, Math.ceil( ( this.quota_bucket().reset.getTime() - Date.now() ) / 60_000 ) )
			return `Лимит в час ${ used }/${ limit || '∞' } (Обновление в ${ reset } МСК, через ${ minutes } ${ this.quota_minutes_unit( minutes ) })`
		}

		quota_minutes_unit( minutes: number ) {
			const last = minutes % 10
			const last_two = minutes % 100
			if( last === 1 && last_two !== 11 ) return 'минута'
			if( last >= 2 && last <= 4 && ( last_two < 12 || last_two > 14 ) ) return 'минуты'
			return 'минут'
		}

		query_limit() {
			return Math.max( 1, Number( this.wordstat_user_record()?.wordstat_query_limit || 200 ) )
		}

		subtask_limit() {
			return Math.max( 1, Number( this.wordstat_user_record()?.wordstat_subtask_limit || 1000 ) )
		}

		regular_daily( next?: boolean ) {
			const record = this.task_record()
			if( next === undefined ) return Boolean( record?.regular_daily )
			if( record?.id ) this.$.$shm_ba_pb.update_record( 'wordstat_tasks', record.id, { regular_daily: next } )
			return next
		}

		@ $mol_mem_key
		reports_enabled_draft( id: string, next?: boolean ) {
			$mol_wire_solid()
			return next ?? Boolean( this.task_record()?.reports_enabled )
		}

		reports_enabled( next?: boolean ) {
			const record = this.task_record()
			const id = String( record?.id || this.dashboard_id() )
			if( next === undefined ) return this.reports_enabled_draft( id )
			this.reports_enabled_draft( id, next )
			if( record?.id && next !== Boolean( record.reports_enabled ) ) {
				this.$.$shm_ba_pb.update_record_async( 'wordstat_tasks', record.id, { reports_enabled: next } )
					.catch( error => {
						this.reports_enabled_draft( id, Boolean( record.reports_enabled ) )
						this.report_error( this.error_message( error ) )
					} )
			}
			return this.reports_enabled_draft( id )
		}

		report_send_enabled() {
			return Boolean(
				this.task_record()?.result_file
				&& ( this.telegram_chat_ids_draft().trim() || this.max_chat_ids_draft().trim() )
				&& !this.report_sending()
			)
		}

		@ $mol_mem
		report_sending( next?: boolean ) {
			return next ?? false
		}

		@ $mol_action
		report_send( next?: Event ) {
			if( next === undefined || !this.report_send_enabled() ) return null
			this.report_error( '' )
			this.report_sending( true )
			this.$.$mol_wire_async( this ).report_send_later().catch( error => {
				this.report_error( this.error_message( error ) )
			} )
			return next
		}

		@ $mol_mem
		report_error( next?: string ) {
			return next ?? ''
		}

		async report_send_later() {
			try {
				await this.pb_json(
					`${ this.$.$shm_ba_pb.url() }/api/hitalama/wordstat/tasks/${ encodeURIComponent( this.dashboard_id() ) }/report`,
					{
						method: 'POST',
						body: JSON.stringify({ period: this.period_kind() }),
					},
				)
			} finally {
				this.report_sending( false )
			}
		}

		report_chat_ids( field: 'telegram_chat_ids' | 'max_chat_ids' ) {
			return String( this.task_record()?.[ field ] || '' )
		}

		report_chat_ids_normalize( value: string ) {
			return [ ... new Set( value.split( /[\s,;]+/ ).map( id => id.trim() ).filter( Boolean ) ) ].join( ', ' )
		}

		@ $mol_mem_key
		report_chat_ids_draft( key: string, next?: string ) {
			$mol_wire_solid()
			const field = key.endsWith( ':max_chat_ids' ) ? 'max_chat_ids' : 'telegram_chat_ids'
			return next ?? this.report_chat_ids( field )
		}

		telegram_chat_ids_draft( next?: string ) {
			return this.report_chat_ids_draft( `${ this.dashboard_id() }:telegram_chat_ids`, next )
		}

		max_chat_ids_draft( next?: string ) {
			return this.report_chat_ids_draft( `${ this.dashboard_id() }:max_chat_ids`, next )
		}

		@ $mol_action
		report_chat_ids_commit( field: 'telegram_chat_ids' | 'max_chat_ids', next?: Event ) {
			if( next === undefined ) return null
			const draft = field === 'telegram_chat_ids' ? this.telegram_chat_ids_draft() : this.max_chat_ids_draft()
			const value = this.report_chat_ids_normalize( draft )
			if( field === 'telegram_chat_ids' ) this.telegram_chat_ids_draft( value )
			else this.max_chat_ids_draft( value )
			const record = this.task_record()
			if( record?.id && value !== this.report_chat_ids( field ) ) {
				this.$.$shm_ba_pb.update_record_async( 'wordstat_tasks', record.id, { [ field ]: value } )
					.catch( error => {
						const previous = this.report_chat_ids( field )
						if( field === 'telegram_chat_ids' ) this.telegram_chat_ids_draft( previous )
						else this.max_chat_ids_draft( previous )
						this.report_error( this.error_message( error ) )
					} )
			}
			return next
		}

		telegram_chat_ids_commit( next?: Event ) {
			return this.report_chat_ids_commit( 'telegram_chat_ids', next )
		}

		max_chat_ids_commit( next?: Event ) {
			return this.report_chat_ids_commit( 'max_chat_ids', next )
		}

		collect_auto_view_key() {
			return `hitalama_wordstat_collect_auto_view:${ this.dashboard_id() }`
		}

		collect_auto_view_run_id( next?: string ) {
			return this.$.$mol_state_local.value< string >( this.collect_auto_view_key(), next ) ?? ''
		}

		collect_completion_sync() {
			const run_id = this.collect_auto_view_run_id()
			if( !run_id ) return null

			const record = this.task_record()
			if( String( record?.collect_run_id || '' ) !== run_id ) return null
			if( String( record?.collect_status || '' ) !== 'done' || !record?.result_file ) return null

			this.Settings().showed( false )
			this.$.$mol_state_local.value( this.task_mode_key(), 'view' )
			this.$.$mol_state_arg.value( 'wordstat_mode', 'view' )
			this.filter_state().reset()
			this.collect_auto_view_run_id( '' )
			return null
		}

		collect_duration_title() {
			if( this.running() || this.collect_active() ) return ''
			const record = this.task_record()
			const started = Date.parse( String( record?.collect_started_at || '' ) )
			const finished = Date.parse( String( record?.collect_finished_at || '' ) )
			if( !Number.isFinite( started ) || !Number.isFinite( finished ) || finished < started ) return ''

			const seconds = ( finished - started ) / 1000
			const text = new Intl.NumberFormat( 'ru-RU', { maximumFractionDigits: 1 } ).format( seconds )
			return ` - ${ text }с`
		}

		collects_body() {
			return [
				this.Collect_regular_row(),
				this.collect_items_total_count() > 0 ? this.Collect_items_table() : this.Collect_empty(),
				this.Reports_row(),
				... this.report_error() ? [ this.Report_error() ] : [],
				... this.collect_error() ? [ this.Collect_error() ] : [],
				this.Load_ready( this.load_ready_key() ),
			]
		}

		collect_error() {
			return String( this.task_record()?.collect_error || '' )
		}

		load_ready_key() {
			const task = this.dashboard_id()
			const opened = this.$.$shm_wordstat_perf.started_at( `${ task }:open` )
			const switched = this.$.$shm_wordstat_perf.started_at( `${ task }:mode_switch:edit` )
			return `${ task }:${ opened }:${ switched }`
		}

		collect_items_done_count() {
			if( this.collect_result_current() ) return Number( this.task_record()?.collect_done || 0 )
			return this.collect_progress_draft().reduce( ( sum, row )=> sum + row.done, 0 )
		}

		collect_items_total_count() {
			return this.collect_result_current()
				? Number( this.task_record()?.collect_total || 0 )
				: this.collect_tasks_count()
		}

		collect_tasks_safe() {
			return this.collect_tasks_count() > 0 ? this.collect_tasks() : []
		}

		collect_form_missing() {
			return [
				this.collect_queries_selected().length ? '' : 'Запросы',
				this.collect_regions_selected().length ? '' : 'Регионы',
				this.collect_period_kinds_selected().length ? '' : 'Периодичность',
				this.collect_devices_selected().length ? '' : 'Устройства',
			].filter( Boolean )
		}

		collect_form_missing_message() {
			const missing = this.collect_form_missing()
			return `Заполните форму: ${ missing.join( ', ' ) || 'Запросы, Регионы' }`
		}

		@ $mol_mem
		collect_items_rows() {
			const progress = this.collect_settings_matches() ? this.collect_progress() : []
			if( this.collect_result_current() && progress.length ) {
				return progress.map( row => [ row.query, `${ row.done }/${ row.total }` ] )
			}

			return this.collect_progress_draft().map( row => [ row.query, `${ row.done }/${ row.total }` ] )
		}

		collect_progress() {
			const value = this.task_record()?.collect_progress as readonly { query?: unknown, done?: unknown, total?: unknown }[] | null | undefined
			if( !Array.isArray( value ) ) return [] as readonly { query: string, done: number, total: number }[]
			return value.map( row => ({
				query: String( row.query || '' ),
				done: Number( row.done || 0 ),
				total: Number( row.total || 0 ),
			}) ).filter( row => row.query )
		}

		@ $mol_mem
		collect_progress_draft() {
			const totals = new Map< string, { done: number, total: number } >()
			for( const item of this.collect_tasks_safe() ) {
				const row = totals.get( item.query ) ?? { done: 0, total: 0 }
				++row.total
				if( this.collect_task_complete( item ) ) ++row.done
				totals.set( item.query, row )
			}
			return [ ... totals ].map( ([ query, row ])=> ({ query, ... row }) )
		}

		collect_item_key( item: Pick< $shm_wordstat_task, 'query' | 'region' | 'period' | 'device' > ) {
			return [ item.query, item.region || 'Все', item.period, item.device ].join( '\u001f' )
		}

		task_items_version() {
			const record = this.task_record()
			return [ record?.result_version, record?.collect_run_id, record?.collect_status ].map( String ).join( ':' )
		}

		@ $mol_mem
		task_items_meta() {
			const id = this.dashboard_id()
			if( !id || id === this.new_task_id() ) return [] as readonly $shm_ba_pb_file_record[]
			const query = `wordstat_task_items\ntask = "${ this.$.$shm_ba_pb.filter_text( id ) }"\nsort\n1\n10000\nid,key,query,region,period,device,status,date_from,date_to,target_from,target_to,row_count,result_stored,sort`
			this.task_items_version()
			return this.$.$shm_ba_pb.records_page_realtime( query )
		}

		@ $mol_mem
		task_items_by_key() {
			return new Map( this.task_items_meta().map( item => [ String( item.key || '' ), item ] ) )
		}

		collect_task_complete( task: $shm_wordstat_task ) {
			if( this.collect_saved_run().ready ) return this.collect_task_saved_complete( task )

			const key = this.collect_item_key( task )
			const item = this.task_items_by_key().get( key )
			if( item?.result_stored ) {
				if( String( item.date_from || '' ) <= task.date_from && String( item.date_to || '' ) >= task.date_to ) return true
				if(
					String( item.status || '' ) === 'done'
					&& String( item.target_from || '' ) <= task.date_from
					&& String( item.target_to || '' ) >= task.date_to
				) return true
			}
			return false
		}

		@ $mol_mem
		collect_saved_run() {
			const record = this.task_record()
			const settings = this.task_settings()
			const started = new Date( String( record?.collect_started_at || '' ) )
			const ready = String( record?.collect_status || '' ) === 'done' && !Number.isNaN( started.getTime() )
			const periods = this.period_kind_codes().filter( period => settings?.period_kinds?.[ period ] )

			return {
				ready,
				queries: new Set( this.query_lines( settings?.queries || '' ) ),
				regions: new Set( this.lines( settings?.regions || '' ).map( region => region === 'Все' ? '' : region ) ),
				periods: new Set( periods ),
				devices: new Set( this.device_codes().filter( device => settings?.devices?.[ device ] ) ),
				ranges: new Map( ready
					? periods.map( period => [ period, this.collect_period_range( period, started ) ] as const )
					: [],
				),
			}
		}

		collect_task_saved_complete( task: $shm_wordstat_task ) {
			const saved = this.collect_saved_run()
			if( !saved.ready ) return false
			if( !saved.queries.has( task.query ) || !saved.regions.has( task.region ) ) return false
			if( !saved.periods.has( task.period ) || !saved.devices.has( task.device ) ) return false
			const range = saved.ranges.get( task.period )
			return range?.from === task.date_from && range.to === task.date_to
		}

		@ $mol_mem
		collect_tasks_missing_count() {
			if( this.collect_result_current() && !this.collect_active() ) return 0
			return this.collect_tasks_safe().reduce( ( count, task )=> count + ( this.collect_task_complete( task ) ? 0 : 1 ), 0 )
		}

		collect_result_current() {
			const record = this.task_record()
			if( !record || !this.collect_settings_matches() ) return false
			if( this.collect_active() ) return true
			if( String( record.collect_status || '' ) !== 'done' ) return false

			const started = new Date( String( record.collect_started_at || '' ) )
			if( Number.isNaN( started.getTime() ) ) return false
			return this.collect_period_kinds_selected().every( period => {
				const current = this.collect_period_range( period )
				const collected = this.collect_period_range( period, started )
				return current.from === collected.from && current.to === collected.to
			} )
		}

		collect_settings_matches() {
			const settings = this.task_settings()
			if( !settings ) return false
			const equal = ( left: readonly string[], right: readonly string[] )=> left.join( '\n' ) === right.join( '\n' )
			return equal( this.query_lines( settings.queries || '' ), this.collect_queries_selected() )
				&& equal( this.lines( settings.regions || '' ), this.collect_regions_selected() )
				&& equal( this.period_kind_codes().filter( key => settings.period_kinds?.[ key ] ), this.collect_period_kinds_selected() )
				&& equal( this.device_codes().filter( key => settings.devices?.[ key ] ), this.collect_devices_selected() )
		}

		table_region_titles( table: $shm_wordstat_data_table, share_hidden = false ): $shm_wordstat_data_table {
			const region = table.head.indexOf( 'Регион' )
			const period = table.head.indexOf( 'Периодичность' )
			const device = table.head.indexOf( 'Устройство' )
			const date_from = table.head.indexOf( 'С' )
			const date_to = table.head.indexOf( 'По' )
			const share = table.head.indexOf( 'Доля' )
			const col_hidden = share_hidden && share !== -1 ? share : -1

			return {
				head: table.head.filter( ( _, index )=> index !== col_hidden ),
				rows: table.rows.map( row => {
					return row
						.map( ( cell, index )=> {
							if( index === region ) return this.region_title( String( cell ) )
							if( index === period ) return this.period_title( String( cell ) )
							if( index === device ) return this.device_title( String( cell ) )
							if( index === date_from || index === date_to ) return this.date_title( String( cell ) )
							return cell
						} )
						.filter( ( _, index )=> index !== col_hidden )
				} ),
			}
		}

		date_title( date: string ) {
			const match = /^(\d{4})-(\d{2})-(\d{2})/.exec( date )
			return match ? `${ match[3] }.${ match[2] }.${ match[1] }` : date
		}

		region_labels() {
			return this.$.$mol_error_fence(
				()=> ( this.Filters_page().Region_select() as any ).label_by_id(),
				()=> new Map< string, string >(),
				promise => $mol_fail_hidden( promise ),
			)
		}

		region_title( id: string ) {
			if( !id || id === 'Все' ) return 'Весь мир'
			return this.region_labels().get( id ) ?? this.region_title_default( id )
		}

		region_title_default( id: string ) {
			return ({
				'1': 'Москва и Московская область',
				'2': 'Санкт-Петербург',
				'3': 'Центр',
				'17': 'Северо-Запад',
				'26': 'Юг',
				'40': 'Поволжье',
				'52': 'Урал',
				'59': 'Сибирь',
				'73': 'Дальний Восток',
				'213': 'Москва',
				'225': 'Россия',
				'10174': 'Санкт-Петербург и Ленинградская область',
			} as Record< string, string >)[ id ] ?? id
		}

		chart_timeseries() {
			return this.data().chart_timeseries().map( ( row: readonly [ string, number, string ] )=> {
				const title = String( row[2] ?? '' )
				const parts = title.split( ' · ' )
				if( parts.length < 2 ) return row

				const query = parts[0]
				const region = parts[1]
				const device = parts[2]
				return [
					row[0],
					row[1],
					[ query, this.region_title( region ), device ? this.device_title( device ) : '' ].filter( Boolean ).join( ' · ' ),
				] as const
			} )
		}

		chart_period_range_pick( next?: readonly string[] ) {
			if( next === undefined ) return null
			if( next.length < 2 ) return next

			this.filter_state().period({
				from: next[0],
				to: next[1],
			})
			return next
		}

		csv_file_name() {
			const title = ( this.task_title_current().trim() || 'Wordstat' )
				.replace( /[<>:"/\\|?*\u0000-\u001F]/g, '_' )
			const period = this.period()
			const date = ( value: string )=> {
				const match = String( value ).match( /^(\d{4})-(\d{2})-(\d{2})/ )
				return match ? `${ match[3] }.${ match[2] }.${ match[1] }` : value
			}
			return `${ title }_${ date( period.from ) }-${ date( period.to ) }.csv`
		}

		@ $mol_action
		task_mode_set( mode: 'edit' | 'view', next?: Event ) {
			if( next === undefined ) return null
			this.Settings().showed( false )
			this.$.$mol_state_local.value( this.task_mode_key(), mode )
			this.$.$mol_state_arg.value( 'wordstat_mode', mode )
			return next
		}

		task_mode_view( next?: Event ) {
			if( next !== undefined && !this.task_mode_view_enabled() ) return next
			if( next !== undefined && this.task_mode() !== 'view' ) {
				this.$.$shm_wordstat_perf.start_mode( this.dashboard_id(), 'view' )
			}
			return this.task_mode_set( 'view', next )
		}

		task_mode_edit( next?: Event ) {
			if( next !== undefined && this.task_mode() !== 'edit' ) {
				this.$.$shm_wordstat_perf.start_mode( this.dashboard_id(), 'edit' )
			}
			return this.task_mode_set( 'edit', next )
		}

		task_mode_view_enabled() {
			return this.has_data()
		}

		task_mode_view_color() {
			return this.task_mode() === 'view' ? 'var(--mol_theme_current)' : ''
		}

		task_mode_edit_color() {
			return this.task_mode() === 'edit' ? 'var(--mol_theme_current)' : ''
		}

		@ $mol_action
		task_title_commit( next?: Event ) {
			if( next === undefined ) return null

			const id = this.dashboard_id()
			if( !id || id === this.new_task_id() ) return next

			const prev = this.task_title_current( id ).trim() || 'Задача'
			const title = this.task_title_draft_by_id( id ).trim()

			if( !title ) {
				this.task_title_draft_by_id( id, prev )
				return next
			}

			if( title !== prev ) this.$.$shm_ba_pb.update_record( 'wordstat_tasks', id, { title } )
			this.task_title_draft_by_id( id, title )

			return next
		}

		@ $mol_action
		task_title_keydown( next?: KeyboardEvent ) {
			if( next?.key !== 'Escape' ) return next ?? null

			const id = this.dashboard_id()
			next.preventDefault()
			this.task_title_draft_by_id( id, this.task_title_current( id ) )
			this.Dashboard_head().Title_edit().focused( false )

			return next
		}

		@ $mol_action
		task_delete( next?: Event ) {
			if( next === undefined ) return null

			const id = this.dashboard_id()
			this.Settings().showed( false )
			if( !id || id === this.new_task_id() ) return next

			const filter_prefix = `hitalama_wordstat_filter:${ id }:`
			for( const key of Object.keys( this.$.$mol_dom_context.localStorage ) ) {
				if( key.startsWith( filter_prefix ) ) this.$.$mol_dom_context.localStorage.removeItem( key )
			}
			this.$.$mol_state_local.value( `hitalama_wordstat_filter_last:${ id }`, null )
			this.$.$mol_state_local.value( `hitalama_wordstat_filter_last:${ id }:collect`, null )
			this.log( '' )
			this.job( null )
			this.running( false )
			this.$.$mol_state_arg.value( 'wordstat_task', this.new_task_id() )
			this.$.$mol_state_arg.value( 'wordstat_mode', null )
			this.$.$mol_state_arg.value( 'wordstat_filter_id', null )
			const user = this.user_id()
			const trigger = `wordstat_tasks/user:${ user }:list`
			this.$.$shm_ba_pb.realtime_live_remove( trigger, id )
			this.$.$mol_wire_async( this ).task_delete_later( id, trigger ).catch( error => console.error( error ) )
			this.refresh_wake()

			return next
		}

		async task_delete_later( id: string, trigger: string ) {
			try {
				await this.pb_json(
					`${ this.$.$shm_ba_pb.url() }/api/collections/wordstat_tasks/records/${ encodeURIComponent( id ) }`,
					{ method: 'DELETE' },
				)
			} catch( error ) {
				this.$.$shm_ba_pb.realtime_live_reload( trigger )
				throw error
			}
		}

		csv_head() {
			return [ 'Запрос', 'Регион', 'ID региона', 'Периодичность', 'Устройство', 'Дата', 'Кол-во', 'Доля' ]
		}

		csv_cell( value: unknown ) {
			const text = String( value ?? '' )
			return /[";\n\r]/.test( text ) ? `"${ text.replaceAll( '"', '""' ) }"` : text
		}

		csv_text() {
			const rows = this.filtered_rows().map( ( row: $shm_wordstat_row )=> [
				row.query,
				this.region_title( row.region ),
				row.region,
				row.period,
				row.device,
				row.date,
				row.count,
				row.share,
			] )

			return [
				... [ this.csv_head(), ... rows ]
					.map( row => row.map( ( cell: unknown )=> this.csv_cell( cell ) ).join( ';' ) ),
			].join( '\r\n' )
		}

		csv_blob() {
			return new Blob([ '\uFEFF', this.csv_text() ], { type: 'text/csv;charset=utf-8' })
		}

		result_rows() {
			return [
				this.Timeseries_chart(),
				this.Data_table(),
			] as readonly $mol_view[]
		}

		@ $mol_action
		chart_rendered( next?: unknown ) {
			if( next === undefined ) return null
			this.$.$shm_wordstat_perf.finish( this.dashboard_id(), 'view' )
			return next
		}

		@ $mol_mem
		refresh( next?: number ) {
			return next ?? 0
		}

		@ $mol_action
		refresh_wake() {
			this.refresh( this.refresh() + 1 )
			return null
		}

		@ $mol_mem_key
		wait_refresh( promise: PromiseLike< unknown > ) {
			promise.then(
				()=> this.refresh_wake(),
				()=> this.refresh_wake(),
			)
			return null
		}

		collect_active() {
			const status = String( this.task_record()?.collect_status || '' )
			return status === 'queued' || status === 'fetch'
		}

		run_title() {
			if( this.collect_queries_selected().length > this.query_limit() ) return `Лимит запросов ${ this.query_limit() } (${ this.collect_queries_selected().length })`
			if( this.collect_tasks_count() > this.subtask_limit() ) return `Лимит подзадач ${ this.subtask_limit() } (${ this.collect_tasks_count() })`
			return this.running() || this.collect_active() ? 'Идет сбор' : `Собрать (${ this.collect_tasks_missing_count() })`
		}

		@ $mol_mem
		collect_tasks_count() {
			const queries = this.collect_queries_selected()
			const regions = this.collect_regions_selected()
			const devices = this.collect_devices_selected()
			const periods = this.collect_period_kinds_selected()

			if( !queries.length || !regions.length || !devices.length || !periods.length ) return 0
			return queries.length * regions.length * devices.length * periods.length
		}

		run_enabled() {
			return !this.running()
				&& !this.collect_active()
				&& this.collect_queries_selected().length <= this.query_limit()
				&& this.collect_tasks_count() <= this.subtask_limit()
				&& this.dashboard_id() !== this.new_task_id()
				&& this.collect_tasks_missing_count() > 0
		}

		has_data() {
			if( this.dashboard_id() === this.new_task_id() ) return false
			return Boolean( this.task_record()?.result_file )
		}

		collecting() {
			return this.task_mode() === 'edit' || !this.has_data()
		}

		@ $mol_mem
		task_item_facets() {
			if( this.has_data() ) {
				const facets = this.data().facets() as {
					queries: string[]
					regions: string[]
					periods: string[]
					devices: string[]
				}
				return { has_data: true, ... facets }
			}
			const settings = this.task_settings()
			const queries = this.query_lines( settings?.queries || '' )
			const regions = this.lines( settings?.regions || '' )
			const devices = this.device_codes().filter( key => settings?.devices?.[ key ] )
			const periods = this.period_kind_codes().filter( key => settings?.period_kinds?.[ key ] )

			return {
				has_data: this.has_data(),
				queries,
				regions,
				devices,
				periods,
			}
		}

		query_filter_visible() {
			return this.collecting() || this.query_available().length > 0
		}

		query_available() {
			return this.collecting() ? [] : this.task_item_facets().queries
		}

		period_kind_filter_visible() {
			return this.collecting() || this.period_kind_available().length > 0
		}

		period_kind_available() {
			return this.collecting() ? [] : this.task_item_facets().periods
		}

		device_filter_visible() {
			return this.collecting() || this.device_available().length > 0
		}

		device_available() {
			return this.collecting() ? [] : this.task_item_facets().devices
		}

		region_filter_visible() {
			return this.collecting() || this.region_available().length > 0
		}

		region_available() {
			return this.collecting()
				? []
				: this.task_item_facets().regions
		}

		lines( text: string ) {
			return [ ... new Set(
				String( text )
					.split( /[\n;,]+/ )
					.map( item => item.trim() )
					.filter( Boolean )
			) ]
		}

		query_lines( text: string ) {
			return [ ... new Set(
				String( text )
					.split( /\n+/ )
					.map( item => item.trim() )
					.filter( Boolean )
			) ]
		}

		device_codes() {
			return [ 'DEVICE_ALL', 'DEVICE_DESKTOP', 'DEVICE_PHONE', 'DEVICE_TABLET' ]
		}

		devices_checked() {
			const devices = ( this.collecting() ? this.state() : this.filter_state() ).devices()
			return this.device_codes().filter( device => devices[ device ] )
		}

		devices_selected() {
			const selected = this.devices_checked()
			const available = this.device_available()
			if( !this.collecting() && selected.length && available.length ) return selected.filter( device => available.includes( device ) )
			return selected
		}

		devices_filter_selected() {
			return this.devices_selected()
		}

		device_title( device: string ) {
			return ({
				DEVICE_ALL: 'Все устройства',
				DEVICE_DESKTOP: 'ПК',
				DEVICE_PHONE: 'Телефоны',
				DEVICE_TABLET: 'Планшеты',
			} as Record< string, string >)[ device ] ?? device
		}

		period_title( period: string ) {
			return ({
				PERIOD_MONTHLY: 'Месяц',
				PERIOD_WEEKLY: 'Неделя',
				PERIOD_DAILY: 'День',
			} as Record< string, string >)[ period ] ?? period
		}

		queries_selected() {
			if( this.collecting() ) return this.query_lines( this.state().queries() )

			const filter = this.filter_state().query_filter()
			return this.query_available().filter( query => filter[ query ] ?? true )
		}

		regions_selected() {
			const selected = this.lines( ( this.collecting() ? this.state() : this.filter_state() ).regions() )
			const available = this.region_available()
			if( !this.collecting() && available.length ) return selected.filter( region => available.includes( region ) )
			return selected
		}

		period() {
			return this.filter_state().period()
		}

		period_all() {
			const periods = this.collecting()
				? this.period_kind_codes().filter( period => this.task_settings()?.period_kinds?.[ period ] )
				: this.period_kind_available()
			const dates = periods
				.flatMap( ( period: string )=> {
					const range = this.period_all_kind( period )
					return [ range.from, range.to ]
				} )
				.sort()
			return {
				from: dates[0] ?? '0001-01-01',
				to: dates[ dates.length - 1 ] ?? '2999-12-31',
			}
		}

		period_all_kind( period: string ) {
			if( !this.collecting() ) {
				const available = this.data().period_available( period )
				if( available ) return available
			}
			return this.collect_period_range( period )
		}

		period_kind() {
			return this.period_kinds_selected()[0] ?? 'PERIOD_MONTHLY'
		}

		period_kind_codes() {
			return [ 'PERIOD_DAILY', 'PERIOD_WEEKLY', 'PERIOD_MONTHLY' ]
		}

		period_kinds_selected() {
			const state = this.collecting() ? this.state() : this.filter_state()
			const selected = this.period_kind_codes().filter( period => state.period_kinds()[ period ] )
			const available = this.period_kind_available()
			if( !this.collecting() && available.length ) return selected.filter( period => available.includes( period ) ).slice( 0, 1 )
			if( !this.collecting() ) return selected.slice( 0, 1 )
			return selected
		}

		@ $mol_mem
		collect_queries_selected() {
			return this.query_lines( this.state().queries() )
		}

		@ $mol_mem
		collect_regions_selected() {
			return this.lines( this.state().regions() )
		}

		@ $mol_mem
		collect_devices_selected() {
			return this.devices_checked()
		}

		@ $mol_mem
		collect_period_kinds_selected() {
			return this.period_kind_codes().filter( period => this.state().period_kinds()[ period ] )
		}

		task_base_list() {
			const queries = this.collect_queries_selected()
			const regions = this.collect_regions_selected()
			const devices = this.collect_devices_selected()
			const periods = this.collect_period_kinds_selected()
			if( !queries.length ) throw new Error( 'Заполни хотя бы один запрос' )
			if( !regions.length ) throw new Error( 'Выбери хотя бы один регион' )
			if( !periods.length ) throw new Error( 'Выбери хотя бы одну периодичность' )
			if( !devices.length ) throw new Error( 'Выбери хотя бы одно устройство' )

			return queries.flatMap( query => {
				return regions.flatMap( region => {
					return periods.flatMap( period => {
						return devices.map( device => ({
							query,
							region: region === 'Все' ? '' : region,
							period,
							device,
						}) )
					} )
				} )
			} )
		}

		period_from_date( date: string, period: string ) {
			if( period !== 'PERIOD_MONTHLY' ) return date

			const value = new Date( `${ date }T00:00:00Z` )
			if( Number.isNaN( value.getTime() ) ) return date

			value.setUTCDate( 1 )
			return value.toISOString().slice( 0, 10 )
		}

		period_to_date( date: string, period: string ) {
			if( period !== 'PERIOD_MONTHLY' ) return date

			const value = new Date( `${ date }T00:00:00Z` )
			if( Number.isNaN( value.getTime() ) ) return date

			value.setUTCMonth( value.getUTCMonth() + 1 )
			value.setUTCDate( 0 )
			return value.toISOString().slice( 0, 10 )
		}

		period_range() {
			const period = this.period()
			const periods = this.period_kinds_selected()
			const period_kind = periods.length === 1 ? periods[0] : ''

			return {
				from: period.from ? this.period_from_date( period.from, period_kind ) : '',
				to: period.to ? this.period_to_date( period.to, period_kind ) : '',
			}
		}

		date_iso( date: Date ) {
			return date.toISOString().slice( 0, 10 )
		}

		collect_period_range( period = this.period_kind(), now = new Date ) {
			const today = now
			const today_utc = new Date( Date.UTC(
				today.getUTCFullYear(),
				today.getUTCMonth(),
				today.getUTCDate(),
			) )

			if( period === 'PERIOD_DAILY' ) {
				const to = new Date( today_utc )
				to.setUTCDate( to.getUTCDate() - 1 )
				const from = new Date( to )
				from.setUTCDate( from.getUTCDate() - 59 )
				return {
					from: this.date_iso( from ),
					to: this.date_iso( to ),
				}
			}

			if( period === 'PERIOD_WEEKLY' ) {
				const to = new Date( today_utc )
				to.setUTCDate( to.getUTCDate() - ( to.getUTCDay() || 7 ) )
				return {
					from: '2018-01-01',
					to: this.date_iso( to ),
				}
			}

			const to = new Date( Date.UTC( today_utc.getUTCFullYear(), today_utc.getUTCMonth(), 0 ) )
			return {
				from: '2018-01-01',
				to: this.date_iso( to ),
			}
		}

		date_list( from: string, to: string, period: string ) {
			const dates: string[] = []
			const current = new Date( `${ this.period_from_date( from, period ) }T00:00:00Z` )
			const finish = new Date( `${ this.period_from_date( to, period ) }T00:00:00Z` )
			if( Number.isNaN( current.getTime() ) || Number.isNaN( finish.getTime() ) ) return dates

			while( current <= finish ) {
				dates.push( current.toISOString().slice( 0, 10 ) )

				if( period === 'PERIOD_MONTHLY' ) current.setUTCMonth( current.getUTCMonth() + 1 )
				else if( period === 'PERIOD_WEEKLY' ) current.setUTCDate( current.getUTCDate() + 7 )
				else current.setUTCDate( current.getUTCDate() + 1 )
			}

			return dates
		}

		@ $mol_mem
		collect_tasks() {
			const ranges = new Map( this.collect_period_kinds_selected().map( kind => {
				const range = this.collect_period_range( kind )
				if( this.date_list( range.from, range.to, kind ).length < 2 ) {
					throw new Error( 'Для динамики выбери период больше одной точки' )
				}
				return [ kind, range ] as const
			} ) )

			return this.task_base_list().map( task => {
				const period = ranges.get( task.period )!
				return {
					... task,
					date_from: period.from,
					date_to: period.to,
				}
			} )
		}

		filtered_rows() {
			this.refresh()
			return this.has_data() ? this.data().filtered_rows() : []
		}

		pb_headers() {
			return {
				... this.$.$shm_ba_pb.json_headers(),
			}
		}

		async pb_json( uri: string, init: RequestInit = {} ) {
			const response = await this.$.$mol_dom_context.fetch( uri, {
				... init,
				headers: {
					...( init.body ? { 'Content-Type': 'application/json' } : {} ),
					... this.pb_headers(),
					... init.headers,
				},
			} )
			const text = await response.text()
			const data = text ? JSON.parse( text ) : null
			if( !response.ok ) throw new Error( String( data?.message || response.statusText ) )
			return data as $shm_ba_pb_file_record
		}

		async run_later() {
			if( this.dashboard_id() === this.new_task_id() ) throw new Error( 'Сначала добавь задачу' )
			const queue = this.collect_tasks()
			if( !queue.length ) {
				this.running( false )
				return
			}
			const job = {
				phase: 'fetch',
				index: 0,
				fetched: 0,
			}
			this.job( job )
			const response = await this.pb_json(
				`${ this.$.$shm_ba_pb.url() }/api/hitalama/wordstat/tasks/${ encodeURIComponent( this.dashboard_id() ) }/run`,
				{
					method: 'POST',
					body: JSON.stringify({
						settings: {
							queries: this.state().queries(),
							regions: this.state().regions(),
							period_kind: this.period_kind(),
							period_kinds: this.state().period_kinds(),
							devices: this.state().devices(),
						},
						queue,
					}),
				},
			)
			this.collect_auto_view_run_id( String( ( response as any )?.run_id || '' ) )

			this.job_save({ ... job, phase: 'done' })
			this.running( false )
		}

		async cancel_later() {
			if( this.dashboard_id() === this.new_task_id() ) return null
			await this.pb_json(
				`${ this.$.$shm_ba_pb.url() }/api/hitalama/wordstat/tasks/${ encodeURIComponent( this.dashboard_id() ) }/cancel`,
				{ method: 'POST' },
			)
			this.running( false )
			return null
		}

		job_save( job: $shm_wordstat_job ) {
			this.job( job )
			return job
		}

		error_message( error: any ) {
			const source = String( error?.message || error || '' )
			const desc = /desc = ([\s\S]+)/.exec( source )?.[1]?.trim()
			return desc || source || 'Неизвестная ошибка'
		}

		run( next?: Event ) {
			if( next === undefined ) return null

			this.collect_auto_view_run_id( '' )
			this.running( true )
			this.$.$mol_wire_async( this ).run_later().catch( error => {
				let job = this.job()
				if( job && job.phase !== 'error' ) {
					this.job_save({ ... job, phase: 'error', error: this.error_message( error ) })
				}
				this.running( false )
			} )
			return next
		}

		cancel( next?: Event ) {
			if( next === undefined ) return null
			this.$.$mol_wire_async( this ).cancel_later().catch( error => {
				console.error( error )
				this.running( false )
			} )
			return next
		}

	}

}
